import type { HealthBar } from './health.ts';
import { healSlots } from './health.ts';
import type { ManaPool } from './mana.ts';

/**
 * Resting and mending.
 * RULE DCC-REST-001 - Rules for Survival p.43 supplies the durations and Health
 * recovery amounts.
 *
 * PROJECT MANA OVERRIDE DCC-MANA-005. The table's combat regeneration is
 * `max(1, floor(effective INT Mod / 2))` every completed round. The owner has
 * ruled that any full hour of safe downtime therefore guarantees full Mana;
 * the printed 5-Mana / half-Max rest recovery is not used by this application.
 *
 *  - Mending: every full hour outside combat or other stress recovers 1 Health
 *    Bar slot and fully restores Mana.
 *  - Short Rest (2 hours): 5 Health Bar slots and fully restores Mana.
 *  - Long Rest (8 hours): full Health Bar and Mana, and removes all instances of
 *    the Fatigued Debuff.
 *  - Full Day's Rest (30 hours): full Health Bar and Mana, and is required to
 *    recover from long-term injuries.
 */
export type RestKind = 'mend-hour' | 'short' | 'long' | 'full-day';

export const REST_HOURS: Record<RestKind, number> = {
  'mend-hour': 1,
  short: 2,
  long: 8,
  'full-day': 30,
};

export interface RestState {
  bar: HealthBar;
  mana: ManaPool;
}

export interface RestResult extends RestState {
  kind: RestKind;
  removesFatigued: boolean;
  recoversLongTermInjuries: boolean;
}

export function applyRest(state: RestState, kind: RestKind): RestResult {
  switch (kind) {
    case 'mend-hour':
      return {
        kind,
        bar: healSlots(state.bar, 1),
        mana: { ...state.mana, current: state.mana.max },
        removesFatigued: false,
        recoversLongTermInjuries: false,
      };
    case 'short':
      return {
        kind,
        bar: healSlots(state.bar, 5),
        mana: { ...state.mana, current: state.mana.max },
        removesFatigued: false,
        recoversLongTermInjuries: false,
      };
    case 'long':
      return {
        kind,
        bar: { ...state.bar, slotsRemaining: state.bar.maxSlots },
        mana: { ...state.mana, current: state.mana.max },
        removesFatigued: true,
        recoversLongTermInjuries: false,
      };
    case 'full-day':
      return {
        kind,
        bar: { ...state.bar, slotsRemaining: state.bar.maxSlots },
        mana: { ...state.mana, current: state.mana.max },
        // Rest tiers are CUMULATIVE by duration: 30 hours contains the 8 of a
        // long rest, which contains the 2 of a short one. Rules p.43 describes
        // a full day's rest as recovering long-term injuries and "also" full
        // Health and Mana - everything a long rest does, and more. A full day
        // therefore clears Fatigued too.
        // See docs/RULES_CONFLICTS.md RESOLVED-006.
        removesFatigued: true,
        recoversLongTermInjuries: true,
      };
  }
}
